import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, XCircle, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [state, setState] = useState("checking");
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) return setState("invalid");
    api
      .get(`/auth/reset-password/${token}`)
      .then((r) => { setEmail(r.data.email); setState("ready"); })
      .catch(() => setState("invalid"));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pw.length < 8) return setError("Password must be at least 8 characters.");
    if (pw !== pw2) return setError("The two passwords do not match.");
    setSaving(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token, password: pw });
      localStorage.setItem("matiqs_token", data.token);
      setUser(data.user);
      toast.success("Password updated — you are signed in");
      navigate("/dashboard");
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || "Could not reset the password.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-5">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
        <img src="/matiqs-logo.png" alt="Matiqs" className="brand-logo h-8 w-auto mb-8" />
        <div className="bg-card border border-border rounded-2xl p-8">
          {state === "checking" && (
            <div data-testid="reset-checking" className="flex items-center gap-3 text-sm text-muted-foreground py-4">
              <Loader2 className="w-5 h-5 animate-spin text-primary" /> Checking your reset link…
            </div>
          )}

          {state === "invalid" && (
            <div data-testid="reset-invalid">
              <XCircle className="w-9 h-9 text-destructive" />
              <h1 className="font-display text-2xl font-bold text-slate-900 dark:text-slate-100 mt-5">Link no longer valid</h1>
              <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                Reset links expire after 60 minutes and can only be used once. Request a fresh one and we will email it right away.
              </p>
              <Button data-testid="request-new-link-button" onClick={() => navigate("/forgot-password")} className="mt-6 font-semibold">
                Request a new link
              </Button>
            </div>
          )}

          {state === "ready" && (
            <>
              <h1 className="font-display text-2xl font-bold text-slate-900 dark:text-slate-100">Set a new password</h1>
              <p className="text-sm text-muted-foreground mt-1.5 mb-6">
                For <span className="font-semibold text-foreground">{email}</span>
              </p>
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <Label htmlFor="new-password">New password</Label>
                  <PasswordInput
                    id="new-password"
                    data-testid="new-password-input"
                    value={pw}
                    onChange={(e) => setPw(e.target.value)}
                    placeholder="At least 8 characters"
                    required
                    className="mt-1.5"
                  />
                </div>
                <div>
                  <Label htmlFor="confirm-password">Confirm new password</Label>
                  <PasswordInput
                    id="confirm-password"
                    data-testid="confirm-password-input"
                    value={pw2}
                    onChange={(e) => setPw2(e.target.value)}
                    placeholder="Repeat it"
                    required
                    className="mt-1.5"
                  />
                </div>
                {error && (
                  <div data-testid="reset-error" className="text-sm text-destructive bg-destructive/10 rounded-md px-3 py-2">
                    {error}
                  </div>
                )}
                <Button
                  type="submit"
                  data-testid="reset-submit-button"
                  disabled={saving}
                  className="w-full h-11 text-sm font-semibold"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Update password & sign in"}
                </Button>
              </form>
            </>
          )}

          <Link
            to="/login"
            className="inline-flex items-center gap-2 text-sm font-medium text-muted-foreground mt-6 hover:text-primary"
          >
            <ArrowLeft className="w-4 h-4" /> Back to sign in
          </Link>
        </div>
      </motion.div>
    </div>
  );
}
